import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) =>
  n == null ? '—' : `$${Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtSigned = (n) => {
  if (n == null) return '—';
  const abs = Math.abs(Number(n));
  const str = `$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return Number(n) > 0 ? `-${str}` : `+${str}`;
};

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const ACCOUNT_ICONS = {
  depository: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-500">
      <path d="M2 6l6-4 6 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 6v6M7 6v6M9 6v6M13 6v6" strokeLinecap="round" />
      <path d="M1 12h14M1 14h14" strokeLinecap="round" />
    </svg>
  ),
  credit: (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-500">
      <rect x="1" y="3" width="14" height="10" rx="2" />
      <path d="M1 7h14" />
      <path d="M4 10h3" strokeLinecap="round" />
    </svg>
  ),
};

const MATCH_BADGE = {
  confirmed: { label: 'Matched', color: 'text-emerald-600 bg-emerald-50' },
  suggested: { label: 'Suggested', color: 'text-amber-600 bg-amber-50' },
  unmatched: { label: 'Unmatched', color: 'text-zinc-500 bg-zinc-50' },
  excluded: { label: 'Excluded', color: 'text-zinc-400 bg-zinc-50' },
};

const ENTITY_TYPE_LABEL = {
  purchase: 'Purchase',
  deposit: 'Deposit',
  transfer: 'Transfer',
  bill_payment: 'Bill Payment',
};

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

const SummaryCards = ({ summary, loading }) => {
  const cards = [
    {
      label: 'Total Balance',
      value: loading ? '...' : fmt(summary?.totalBalance),
      sub: `${summary?.accounts || 0} account${summary?.accounts !== 1 ? 's' : ''}`,
    },
    {
      label: 'Transactions',
      value: loading ? '...' : (summary?.totalTransactions || 0).toLocaleString(),
      sub: 'All time',
    },
    {
      label: 'Unmatched',
      value: loading ? '...' : (summary?.unmatchedTransactions || 0).toLocaleString(),
      sub: 'Need review',
      highlight: summary?.unmatchedTransactions > 0,
    },
    {
      label: 'Last Synced',
      value: loading ? '...' : timeAgo(summary?.lastSyncedAt),
      sub: summary?.lastSyncedAt ? new Date(summary.lastSyncedAt).toLocaleDateString() : '',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-zinc-100 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{c.label}</p>
          <p className={`text-lg font-medium ${c.highlight ? 'text-amber-600' : 'text-zinc-900'}`}>{c.value}</p>
          {c.sub && <p className="text-[11px] text-zinc-400 mt-0.5">{c.sub}</p>}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Account cards
// ---------------------------------------------------------------------------

const AccountCard = ({ account, selected, onClick }) => {
  const icon = ACCOUNT_ICONS[account.account_type] || ACCOUNT_ICONS.depository;
  const isCredit = account.account_type === 'credit';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white border rounded-lg p-4 transition-all duration-200 hover:border-zinc-300 ${
        selected ? 'border-zinc-900 ring-1 ring-zinc-900' : 'border-zinc-100'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center">{icon}</div>
          <div>
            <p className="text-sm font-medium text-zinc-900">{account.name}</p>
            {account.mask && <p className="text-[11px] text-zinc-400">····{account.mask}</p>}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
          isCredit ? 'text-purple-600 bg-purple-50' : 'text-blue-600 bg-blue-50'
        }`}>
          {account.account_subtype || account.account_type}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-zinc-50">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">
            {isCredit ? 'Balance' : 'Available'}
          </p>
          <p className="text-sm font-medium text-zinc-900">
            {fmt(isCredit ? account.current_balance : (account.available_balance ?? account.current_balance))}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">
            {isCredit ? 'Credit Limit' : 'Current'}
          </p>
          <p className="text-sm font-medium text-zinc-900">
            {fmt(isCredit ? account.credit_limit : account.current_balance)}
          </p>
        </div>
      </div>
      {account.institution_name && (
        <p className="text-[11px] text-zinc-400 mt-2">{account.institution_name}</p>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Transaction row
// ---------------------------------------------------------------------------

const TransactionRow = ({ txn }) => {
  const amount = parseFloat(txn.amount) || 0;
  const isDebit = amount > 0;
  const matchBadge = MATCH_BADGE[txn.match_status] || MATCH_BADGE.unmatched;
  const entityLabel = ENTITY_TYPE_LABEL[txn.source_entity_type] || txn.source_entity_type;
  const displayName = txn.merchant_name || txn.counterparty_name || txn.raw_name || 'Unknown';
  const accountName = txn.banking_accounts?.name;

  return (
    <tr className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50 transition-colors">
      <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
        {fmtDate(txn.date)}
        {txn.pending && <span className="ml-1.5 text-[9px] text-amber-500 uppercase font-medium">Pending</span>}
      </td>
      <td className="px-4 py-3">
        <div>
          <p className="text-sm text-zinc-900 truncate max-w-[220px]">{displayName}</p>
          {accountName && (
            <p className="text-[10px] text-zinc-400">{accountName}{txn.banking_accounts?.mask ? ` ····${txn.banking_accounts.mask}` : ''}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {txn.category && (
          <span className="text-[10px] text-zinc-500 bg-zinc-50 px-2 py-0.5 rounded lowercase">
            {txn.category.replace(/_/g, ' ')}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {entityLabel && (
          <span className="text-[10px] text-zinc-500 bg-zinc-50 px-2 py-0.5 rounded">
            {entityLabel}
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${matchBadge.color}`}>
          {matchBadge.label}
        </span>
      </td>
      <td className={`px-4 py-3 text-sm text-right whitespace-nowrap font-medium ${isDebit ? 'text-red-600' : 'text-emerald-600'}`}>
        {fmtSigned(amount)}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Transactions table
// ---------------------------------------------------------------------------

const TransactionsTable = ({ transactions, total, loading, page, onPageChange }) => {
  const PAGE_SIZE = 100;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading && !transactions.length) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!transactions.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-zinc-400">No transactions found</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-400">{total.toLocaleString()} transaction{total !== 1 ? 's' : ''}</p>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-xs text-zinc-400">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
      <div className="bg-white border border-zinc-100 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-50">
              <th className="text-left text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Date</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Description</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Category</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Type</th>
              <th className="text-left text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Match</th>
              <th className="text-right text-[10px] uppercase tracking-wider text-zinc-400 font-medium px-4 py-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn) => (
              <TransactionRow key={txn.id} txn={txn} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

const FilterBar = ({ accounts, selectedAccount, onAccountChange, matchFilter, onMatchFilterChange }) => (
  <div className="flex flex-wrap items-center gap-3">
    <select
      value={selectedAccount || ''}
      onChange={(e) => onAccountChange(e.target.value || null)}
      className="text-xs bg-white border border-zinc-200 rounded-md px-3 py-2 text-zinc-700 outline-none focus:ring-1 focus:ring-zinc-900"
    >
      <option value="">All accounts</option>
      {accounts.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}{a.mask ? ` ····${a.mask}` : ''}
        </option>
      ))}
    </select>
    <select
      value={matchFilter || ''}
      onChange={(e) => onMatchFilterChange(e.target.value || null)}
      className="text-xs bg-white border border-zinc-200 rounded-md px-3 py-2 text-zinc-700 outline-none focus:ring-1 focus:ring-zinc-900"
    >
      <option value="">All statuses</option>
      <option value="unmatched">Unmatched</option>
      <option value="confirmed">Matched</option>
      <option value="suggested">Suggested</option>
      <option value="excluded">Excluded</option>
    </select>
  </div>
);

// ---------------------------------------------------------------------------
// Empty / Not Connected
// ---------------------------------------------------------------------------

const NotConnected = ({ onNavigateToIntegrations }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400">
        <path d="M3 9l9-6 9 6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 9v9M9 9v9M15 9v9M19 9v9" strokeLinecap="round" />
        <path d="M2 18h20M2 21h20" strokeLinecap="round" />
      </svg>
    </div>
    <h3 className="text-lg font-light text-zinc-900 mb-1">No banking data yet</h3>
    <p className="text-sm text-zinc-400 mb-6 max-w-xs">
      Sync your bank accounts from QuickBooks or connect via Plaid to see your transactions here.
    </p>
    <button
      onClick={onNavigateToIntegrations}
      className="px-5 py-2 text-[11px] uppercase tracking-[0.15em] text-white bg-zinc-900 hover:bg-zinc-800 rounded-md transition-colors duration-300"
    >
      Go to Integrations
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const BankingView = ({ loaded, onNavigateToIntegrations }) => {
  const [tab, setTab] = useState('transactions');
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [matchFilter, setMatchFilter] = useState(null);
  const [loading, setLoading] = useState({ summary: true, accounts: true, transactions: true });
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiFetch('/api/banking/summary');
      const data = await res.json();
      setSummary(data);
    } catch {
      // silent — summary is optional
    } finally {
      setLoading((p) => ({ ...p, summary: false }));
    }
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/banking/accounts');
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch {
      setError('Failed to load accounts');
    } finally {
      setLoading((p) => ({ ...p, accounts: false }));
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading((p) => ({ ...p, transactions: true }));
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      params.set('offset', String(page * 100));
      if (selectedAccount) params.set('account_id', selectedAccount);
      if (matchFilter) params.set('match_status', matchFilter);

      const res = await apiFetch(`/api/banking/transactions?${params}`);
      const data = await res.json();
      setTransactions(data.transactions || []);
      setTxnTotal(data.total || 0);
    } catch {
      setError('Failed to load transactions');
    } finally {
      setLoading((p) => ({ ...p, transactions: false }));
    }
  }, [page, selectedAccount, matchFilter]);

  useEffect(() => {
    fetchSummary();
    fetchAccounts();
  }, [fetchSummary, fetchAccounts]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedAccount, matchFilter]);

  const hasData = !loading.accounts && accounts.length > 0;

  if (!loading.accounts && !loading.summary && accounts.length === 0) {
    return (
      <div className="max-w-[1100px] mx-auto">
        <div
          className="mb-8 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '300ms' }}
        >
          <h1 className="text-2xl font-light text-zinc-900 mb-1">Banking</h1>
          <p className="text-sm text-zinc-400">Bank accounts and transaction feed</p>
        </div>
        <NotConnected onNavigateToIntegrations={onNavigateToIntegrations} />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto">
      {/* Header */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '300ms' }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-1">Banking</h1>
        <p className="text-sm text-zinc-400">Bank accounts and transaction feed</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <span className="text-sm text-red-600">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 text-xs">Dismiss</button>
        </div>
      )}

      {/* Summary cards */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '400ms' }}
      >
        <SummaryCards summary={summary} loading={loading.summary} />
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-6 bg-zinc-100 rounded-lg p-1 w-fit transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '500ms' }}
      >
        {[
          { id: 'transactions', label: 'Transactions' },
          { id: 'accounts', label: 'Accounts' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
              tab === t.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '600ms' }}
      >
        {tab === 'transactions' && (
          <div className="space-y-4">
            <FilterBar
              accounts={accounts}
              selectedAccount={selectedAccount}
              onAccountChange={setSelectedAccount}
              matchFilter={matchFilter}
              onMatchFilterChange={setMatchFilter}
            />
            <TransactionsTable
              transactions={transactions}
              total={txnTotal}
              loading={loading.transactions}
              page={page}
              onPageChange={setPage}
            />
          </div>
        )}

        {tab === 'accounts' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {loading.accounts ? (
              <div className="col-span-2 flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
              </div>
            ) : (
              accounts.map((a) => (
                <AccountCard
                  key={a.id}
                  account={a}
                  selected={selectedAccount === a.id}
                  onClick={() => {
                    setSelectedAccount(selectedAccount === a.id ? null : a.id);
                    setTab('transactions');
                  }}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BankingView;
